import { OnboardingOptionPill } from "@/features/onboarding/components/onboarding-option-pill";
import { OnboardingPillButton } from "@/features/onboarding/components/onboarding-pill-button";
import { OnboardingStepShellV5 } from "@/features/onboarding/components/onboarding-step-shell-v5";
import { useGetFitnessProfile } from "@/hooks/requests/fitness-profile/use-get-fitness-profile";
import { useUpdateFitnessProfile } from "@/hooks/requests/fitness-profile/use-update-fitness-profile";
import { useToast } from "@/hooks/ui/use-toast";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { triggerHaptic } from "@/utils/haptics";
import { useEffect, useRef, useState } from "react";

const OPTIONS = [
  { id: "feel-more-confident", label: "Feel more confident" },
  { id: "have-more-energy", label: "Have more energy" },
  { id: "feel-stronger-fitter", label: "Feel stronger / fitter" },
  { id: "feel-in-control-around-food", label: "Feel in control around food" },
  { id: "be-more-consistent", label: "Be more consistent" },
  { id: "improve-my-health", label: "Improve my health" },
];

const ProgressChangePage = () => {
  const { navigateToNextStep, preloadNextRoute } = useStepRouteInfo();
  const { fitnessProfile } = useGetFitnessProfile();
  const { updateFitnessProfileAsync, isUpdating } = useUpdateFitnessProfile();
  const { showError } = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (hydratedRef.current) return;
    if (fitnessProfile?.progressChange?.length) {
      setSelected(new Set(fitnessProfile.progressChange));
      hydratedRef.current = true;
    }
  }, [fitnessProfile?.progressChange]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleContinue = async () => {
    if (selected.size === 0 || isUpdating) return;
    triggerHaptic();
    try {
      // Only this screen's field — PUT merges, so the other answers are preserved.
      await Promise.all([
        preloadNextRoute(),
        updateFitnessProfileAsync({ progressChange: Array.from(selected) }),
      ]);
      navigateToNextStep();
    } catch (error) {
      showError(error instanceof Error ? error.message : "An unexpected error occurred");
    }
  };

  return (
    <OnboardingStepShellV5
      title="What would progress change for you?"
      subtitle="Select all that apply."
      footer={
        <OnboardingPillButton
          text="Continue"
          onClick={handleContinue}
          disabled={selected.size === 0 || isUpdating}
        />
      }
    >
      <div className="flex flex-col gap-4">
        {OPTIONS.map((option) => (
          <OnboardingOptionPill
            key={option.id}
            label={option.label}
            selected={selected.has(option.id)}
            onClick={() => toggle(option.id)}
          />
        ))}
      </div>
    </OnboardingStepShellV5>
  );
};

export default ProgressChangePage;
